import * as Sentry from '@sentry/react';
import { backendApi } from '_config';
import Api from '_services/Api';
import { MembershipQueryApi } from '_services/Api/Membership/MembershipApi';
import { AxiosResponse } from 'axios';

import ApiWithFileUpload from '_services/Api/WithFileUpload';
import CookieService from '_services/Cookies';
import LocalStorageCoupon from '_services/LocalStorage/Coupon/LocalStorageCoupon';
import { getAnonId } from '_services/MarketingTouch';
import { UserShape, UserUpdateDataShape } from '_types/User';
import Cookie from 'js-cookie';
import {
    CommonRegistrationData,
    DeleteUserData,
    FacebookRegistrationData,
    ForgotPasswordData,
    FormRegistrationData,
    GoogleRegistrationData,
    LoginData,
    ResetPasswordData,
} from './type';

type couponAbleDataShape = {
    coupon_code?: string;
};

/**
 * Checks whether there is a coupon code in the LocalStorage,
 * and appends it to data if it exists
 *
 * @param {couponAbleDataShape} data
 * @returns {couponAbleDataShape}
 */
const checkAddCoupon = (data: couponAbleDataShape): couponAbleDataShape => {
    const couponCode = LocalStorageCoupon.get();
    if (couponCode) {
        /**
         * If the user has a couponCode in the LocalStorage,
         * then we add that to the registration api post,
         * because that will save the used coupon relationship in the API
         */
        data.coupon_code = couponCode;
    }
    return data;
};
/**
 *
 * @param data
 * @returns
 */
const addExtraRegistrationData = (
    data: CommonRegistrationData,
): CommonRegistrationData => {
    const invitedByCode = CookieService.getInvitedBy();
    if (invitedByCode) {
        /**
         * If the cookie shows that this user has been invited by somebody,
         * then we add that code to the registration api post,
         * because that will store the invited/invitor relationship in the API
         */
        data.invited_by = invitedByCode;
    }

    data = checkAddCoupon(data) as CommonRegistrationData;

    const anonId = getAnonId();
    if (anonId) {
        /**
         * Marketing attribution: backend backfills marketing_touches rows for
         * this anon_id with the new user_id. Omit the field if absent — never
         * mint a fresh anon_id at signup time (would link to no prior touches).
         */
        data.anon_id = anonId;
    }

    return data;
};

/**
 * Helper object used for doing user-related API communication with the Backend
 */
const UserApi = {
    /**
     * Registers a user with the data given in the registration form
     *
     * @param {FormRegistrationData} data
     * @returns {Promise<AxiosResponse>}
     */
    async register(
        data: FormRegistrationData,
    ): Promise<AxiosResponse<UserShape>> {
        data = addExtraRegistrationData(data) as FormRegistrationData;

        return Api.post('register', data);
    },

    /**
     * Registers user with data from Facebook
     * @param {FacebookRegistrationData} data
     * @returns {Promise<AxiosResponse>}
     */
    async registerWithFacebook(
        data: FacebookRegistrationData,
    ): Promise<AxiosResponse<UserShape>> {
        data = addExtraRegistrationData(data) as FacebookRegistrationData;

        return Api.post('register/facebook', data);
    },

    /**
     * Registers user with data from Google
     * @param {GoogleRegistrationData} data
     * @returns {Promise<AxiosResponse>}
     */
    async registerWithGoogle(
        data: GoogleRegistrationData,
    ): Promise<AxiosResponse<UserShape>> {
        data = addExtraRegistrationData(data) as GoogleRegistrationData;

        return Api.post('register/google', data);
    },

    /**
     * Tries to login user with the given data
     * @param {LoginData} data
     * @returns Promise<AxiosResponse>
     */
    async login(data: LoginData): Promise<AxiosResponse<{ user: UserShape }>> {
        data = checkAddCoupon(data) as LoginData;

        return Api.post('login', data);
    },

    /**
     * Tries to send a login code to the user with the given email
     * @param {string} email
     * @returns Promise<AxiosResponse>
     */
    async sendLoginCode(
        email: string,
        language: 'no' | 'en',
        name?: string,
    ): Promise<AxiosResponse<{ success?: string }>> {
        return Api.post('login-otp', {
            email,
            preferred_language: language,
            name,
        });
    },

    /**
     * Tries to send a login code to the user with the given email
     * @param {string} email
     * @returns Promise<AxiosResponse>
     */
    async registerWithEmailOtp(
        email: string,
        name?: string,
        country?: string,
        language?: string,
    ): Promise<AxiosResponse<{ success?: string }>> {
        return Api.post('register-otp', {
            email,
            type: 'register',
            name,
            country,
            preferred_language: language,
            anon_id: getAnonId() ?? undefined,
        });
    },

    /**
     * Verifies the login code sent to the user with the given email
     * @param {string} email
     * @param {string} code
     * @returns Promise<AxiosResponse>
     */
    async verifyLoginCode(
        email: string,
        code: string,
        type: 'login' | 'register',
        country?: string,
        language?: string,
        name?: string,
    ): Promise<AxiosResponse<{ user: UserShape }>> {
        return Api.post('verify-otp', {
            email,
            code,
            type,
            country_id: country,
            preferred_language: language,
            name,
            anon_id:
                type === 'register' ? (getAnonId() ?? undefined) : undefined,
        });
    },

    /**
     * Tries to login user with the given facebook id
     * @param {LoginData} data
     * @returns Promise<AxiosResponse>
     */
    async loginWithFacebook(
        facebookId: string,
    ): Promise<AxiosResponse<UserShape>> {
        type FacebookLoginDataShape = {
            facebook_id: string;
            coupon_code?: string;
        };
        let data: FacebookLoginDataShape = {
            facebook_id: facebookId,
        };
        data = checkAddCoupon(data) as FacebookLoginDataShape;

        return Api.post('login/facebook', data);
    },

    /**
     * Tries to login user with the given google id
     * @param {LoginData} data
     * @returns Promise<AxiosResponse>
     */
    async loginWithGoogle(googleId: string): Promise<AxiosResponse<UserShape>> {
        type GoogleLoginDataShape = {
            google_id: string;
            coupon_code?: string;
        };
        let data: GoogleLoginDataShape = {
            google_id: googleId,
        };
        data = checkAddCoupon(data) as GoogleLoginDataShape;

        return Api.post('login/google', data);
    },

    /**
     * Gets user by Facebook id if it exists
     * @param {string} facebookId
     * @returns {Promise<AxiosResponse>}
     */
    async getByFacebookId(
        facebookId: string,
    ): Promise<AxiosResponse<UserShape>> {
        type FacebookGetDataShape = {
            facebookId: string;
            coupon_code?: string;
        };
        let data: FacebookGetDataShape = {
            facebookId: facebookId,
        };
        data = checkAddCoupon(data) as FacebookGetDataShape;

        let getQuery = 'facebook_id=' + data.facebookId;
        if (data.coupon_code) {
            getQuery += '&coupon_code=' + data.coupon_code;
        }

        return Api.get('get-facebook-user?' + getQuery);
    },

    /**
     * Gets user by Google id if it exists
     * @param {string} getByGoogleId
     * @returns {Promise<AxiosResponse>}
     */
    async getByGoogleId(googleId: string): Promise<AxiosResponse<UserShape>> {
        type GoogleGetDataShape = {
            googleId: string;
            coupon_code?: string;
        };
        let data: GoogleGetDataShape = {
            googleId: googleId,
        };
        data = checkAddCoupon(data) as GoogleGetDataShape;

        let getQuery = 'google_id=' + data.googleId;
        if (data.coupon_code) {
            getQuery += '&coupon_code=' + data.coupon_code;
        }

        return Api.get('get-google-user?' + getQuery);
    },

    /**
     * Triggers sending password reset for the given email
     * @param {ForgotPasswordData} data
     * @returns {Promise<AxiosResponse>}
     */
    async forgotPassword(
        data: ForgotPasswordData,
    ): Promise<AxiosResponse<{ success?: string }>> {
        try {
            const response = await Api.post('send-reset-password-email', data);
            return response;
        } catch (error) {
            console.error('Forgot password error. Error:', error);
            Sentry.captureException(error);
            throw error;
        }
    },

    /**
     * Gets referral count from the current user session
     *
     * @returns {Promise<AxiosResponse>}
     */
    async getReferralCount(): Promise<AxiosResponse<{ count: number }>> {
        return Api.get('referral-count');
    },

    /**
     * Resets password to the new one provided
     *
     * @param {ResetPasswordData} data
     * @returns  {Promise<AxiosResponse>}
     */
    async resetPassword(
        data: ResetPasswordData,
    ): Promise<AxiosResponse<{ success?: string; error?: string }>> {
        try {
            const response = await Api.post('reset-password', data);
            return response;
        } catch (error) {
            console.error('Reset password error. Error:', error);
            Sentry.captureException(error);
            throw error;
        }
    },

    /**
     * Gets data for the current user from the session
     * @returns  {Promise<AxiosResponse>}
     */
    async get(): Promise<AxiosResponse<UserShape>> {
        return Api.get('user');
    },

    /**
     * Pings backend with current user activity metadata
     */
    async pingActivity(data: {
        page?: string;
        client?: string;
        locale?: string;
    }): Promise<AxiosResponse<{ status: string }>> {
        try {
            const token = Cookie.get('XSRF-TOKEN');
            if (
                typeof navigator !== 'undefined' &&
                typeof navigator.sendBeacon === 'function' &&
                token
            ) {
                const beaconUrl = backendApi + 'user/activity/ping';
                const beaconPayload = JSON.stringify({
                    ...data,
                    _token: token,
                });
                const queued = navigator.sendBeacon(
                    beaconUrl,
                    new Blob([beaconPayload], {
                        type: 'application/json',
                    }),
                );

                if (queued) {
                    return Promise.resolve({
                        data: { status: 'queued' },
                        status: 202,
                        statusText: 'Accepted',
                        headers: {},
                        config: { url: beaconUrl, method: 'POST' },
                    } as AxiosResponse<{ status: string }>);
                }
            }
        } catch (err) {
            // Silently fall back to standard call
            console.warn('sendBeacon pingActivity failed, falling back', err);
        }

        return Api.post('user/activity/ping', data);
    },

    /**
     * Logs out user
     * @returns  {Promise<AxiosResponse>}
     */
    logout(): Promise<AxiosResponse<void>> {
        return Api.post('logout');
    },
    /**
     * Update user data
     */
    async updateUser(
        data: UserUpdateDataShape,
    ): Promise<AxiosResponse<UserShape>> {
        return Api.post('user/update', data);
    },

    /**
     * Delete user
     *
     * @param {DeleteUserData} data
     * @returns  {Promise<AxiosResponse>}
     */
    async delete(data: DeleteUserData): Promise<AxiosResponse<void>> {
        return Api.post('user/delete', data);
    },

    /**
     * Update profile picture
     */
    async updateProfilePicture(
        data: FormData,
    ): Promise<AxiosResponse<UserShape>> {
        return ApiWithFileUpload.post('user/update', data);
    },

    /**
     *
     */
    async getUserByUsername(
        username: string,
    ): Promise<AxiosResponse<UserShape>> {
        return Api.get('user/get-by-username', { params: { username } });
    },
    /**
     * clear cookies
     */
    async clearCookies(): Promise<AxiosResponse<void>> {
        return Api.post('clear-cookies', []);
    },

    async setOnboardingSteps<T>(data: T): Promise<AxiosResponse<void>> {
        return Api.post('user/onboarding-steps/set', {
            onboarding_steps: data,
        });
    },

    async refreshMembership(): Promise<AxiosResponse<void>> {
        const response = await Api.post('user/membership/refresh');

        // Ensure membership-related queries get fresh data after refresh
        try {
            const { dispatch } = await import('_services/Redux/store');
            dispatch(
                MembershipQueryApi.util.invalidateTags(['TrialEligibility']),
            );
        } catch (error) {
            // Avoid breaking refresh flow if store import fails
            console.error(
                'Failed to invalidate trial eligibility cache',
                error,
            );
        }

        return response;
    },
};

export default UserApi;
